import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Paper, Grid, Dialog, Button, Typography, Box } from '@mui/material';

import Select from '../select/Select';
import TextField from '../text-field/TextField';
import ArrowButton from '../UI/go-back/GoBack';

import {
  useGetAuthenticationQuery,
  useGetCollectionsQuery,
  useGetProfilesQuery,
  useCreateCollectionMutation,
} from '../../services/ADapi';

import CollectionsConfigurate from './CollectionsConfigurate';

import './Collections.scss';
import appStyles from '../app/App.module.scss';

import success from '../../icons/success.svg';
import { notificationSuccess } from '../../store/notificationReducer';

const CollectionsForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: collections = [] } = useGetCollectionsQuery(user_id);
  const { data: profiles = [] } = useGetProfilesQuery(user_id);
  const [createCollection] = useCreateCollectionMutation();

  const [name, setName] = useState('');
  const [nameIsBlur, setNameIsBlur] = useState(false);
  const [nameError, setNameError] = useState(
    'Collection name field is required'
  );
  const [policy, setPolicy] = useState('');
  const [initialPolicy, setInitialPolicy] = useState('');
  const [formIsValid, setFormIsValid] = useState(false);
  const [formIsSuccess, setFormIsSuccess] = useState(false);
  const selectedUserIds = useSelector(
    (state) => state.tree.selectedUsersIdsForBackend
  );
  const [createCollectionData, setCreateCollectionData] = useState({
    plan_code: 'RESELLER',
    priority: collections.length + 1,
    profile: policy,
    domains: [],
    groups: [],
  });
  const [isDialogShow, setDialogState] = useState(false);

  useEffect(() => {
    nameError ? setFormIsValid(false) : setFormIsValid(true);
  }, [nameError]);

  useEffect(() => {
    setInitialPolicy(
      ...profiles
        .filter((option) => option.name === 'Default')
        .map((item) => item.id)
    );
    setCreateCollectionData((prevState) => ({
      ...prevState,
      profile: initialPolicy,
      ad_users: selectedUserIds,
    }));
  }, [profiles, initialPolicy, selectedUserIds]);

  const nameHandler = (e) => {
    setName(e.target.value);

    if (!e.target.value) {
      setNameError('Name field is required');
    } else {
      setNameError('');
      setCreateCollectionData({
        ...createCollectionData,
        name: e.target.value,
      });
    }
  };

  const policyHandler = (e) => {
    setPolicy(e.target.value);
    setCreateCollectionData({
      ...createCollectionData,
      profile: e.target.value,
    });
  };

  const blurHandler = (e) => {
    switch (e.target.name) {
      case 'name':
        setNameIsBlur(true);
        break;
      default:
        break;
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();

    setFormIsSuccess(!formIsSuccess);
    setFormIsValid(false);
    setNameError(' ');

    try {
      await createCollection({
        user_id,
        payload: createCollectionData,
      }).unwrap();
      dialogStateHandler();
    } catch (error) {
      console.error(error);
      dispatch(
        notificationSuccess({
          isNotification: true,
          type: 'error',
          message: error.data,
        })
      );
    }
  };

  const dialogStateHandler = () => {
    if (isDialogShow) {
      navigate('/collections');
    }

    setDialogState((prevState) => !prevState);
  };

  const successCircleCls = appStyles['circle'] + ' ' + appStyles['success'];

  return (
    <div className="collections-form-wrapper">
      <ArrowButton url="/collections" direction="left" title="Collections" />
      <Paper sx={{ position: 'relative' }}>
        <Grid container columnGap={1} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <TextField
              id="collection"
              label="Name"
              value={name}
              size="small"
              onChange={nameHandler}
              onBlur={blurHandler}
            />
            {nameIsBlur && nameError && (
              <span className="validate-error">{nameError}</span>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <Select
              label="Policy"
              value={policy}
              size="small"
              onChange={policyHandler}
            >
              {profiles.map((option) => (
                <option key={Math.random()} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Grid>
        </Grid>
        <CollectionsConfigurate collectionName={name} />
        <div className="collections-form-create">
          <span className="collections-form-create-counter">
            Selected: {selectedUserIds.length} Users
          </span>
          <Button
            type="submit"
            variant="contained"
            disabled={!formIsValid}
            size="small"
            onClick={submitForm}
          >
            Create Collection
          </Button>
        </div>
      </Paper>

      <Dialog open={isDialogShow}>
        <Box className={successCircleCls} sx={{ alignSelf: 'center' }}>
          <img src={success} width={28} height={28} alt="Collection Success" />
        </Box>
        <Typography variant="h6" sx={{ pt: '12px', pb: 3 }}>
          Collection created
        </Typography>
        <ul className="collections-create-success-list">
          <li>{name}</li>
          <li>{policy !== '' ? policy : initialPolicy}</li>
          <li>{selectedUserIds.length} Users</li>
        </ul>
        <Button variant="contained" onClick={dialogStateHandler}>
          Got it
        </Button>
      </Dialog>
    </div>
  );
};

export default CollectionsForm;
